import { useState } from 'react';
import Logo from './Logo';
import { getOrCreateUserToken } from '../api';

const CONSENT_KEY = 'pointspilot_beta_consent_v2';

export function hasAcceptedBetaConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'accepted';
  } catch {
    return false;
  }
}

export function recordBetaConsent(payload) {
  try {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    localStorage.setItem(
      CONSENT_KEY + '_meta',
      JSON.stringify({ ...payload, acceptedAt: new Date().toISOString() })
    );
    // Generate the per-user token so subsequent API calls land on this
    // user's own trip rather than the shared legacy one.
    getOrCreateUserToken();
  } catch {}
}

export default function BetaConsent({ onAccept }) {
  const [name, setName] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeNda, setAgreeNda] = useState(false);

  const canAccept = name.trim().length >= 2 && agreeTerms && agreeNda;

  const handleAccept = () => {
    if (!canAccept) return;
    recordBetaConsent({ name: name.trim() });
    onAccept?.();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-6 bg-brand-cream">
      <div className="relative bg-navy-light border border-brand-ink/10 rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="p-6 sm:p-8 space-y-6">
          {/* Brand header */}
          <div className="flex items-center justify-center">
            <Logo height={48} />
          </div>

          <div className="text-center">
            <div className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] text-brand-blue bg-brand-blue/10 rounded-full px-3 py-1">
              Private Beta
            </div>
            <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold text-brand-ink">
              Welcome to PointsPilot
            </h1>
            <p className="mt-2 text-sm text-brand-ink/70">
              Before we get started, please read and accept the beta tester terms below.
              Your access is personal and confidential.
            </p>
          </div>

          {/* Terms */}
          <section className="space-y-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-brand-ink/60">
              Beta Tester Agreement
            </h2>
            <div className="bg-brand-cream/60 border border-brand-ink/10 rounded-xl p-4 text-sm text-brand-ink/80 space-y-3 max-h-56 overflow-y-auto leading-relaxed">
              <p>
                <strong>1. Beta software.</strong> PointsPilot is in active development.
                Features may change, break, or disappear without notice. Award
                availability, pricing, sweet spots, and AI suggestions are estimates
                and must be verified directly with the airline, hotel, or transfer
                partner before booking. Nothing here is a guaranteed quote.
              </p>
              <p>
                <strong>2. No financial advice.</strong> Recommendations about
                transfers, redemptions, and credit card programs are informational
                only. You are responsible for your own bookings, transfers, taxes,
                fees, and any consequences thereof. Points transfers are generally
                irreversible.
              </p>
              <p>
                <strong>3. Your data.</strong> Point balances and trip details you
                enter are stored locally in your browser and on PointsPilot servers
                to power your trip plan. We will not sell your data. You may request
                deletion at any time by emailing the operator.
              </p>
              <p>
                <strong>4. Feedback.</strong> By using the beta you grant PointsPilot
                a non-exclusive, royalty-free license to use feedback you provide
                (bug reports, suggestions, screenshots) to improve the product.
              </p>
              <p>
                <strong>5. No warranty.</strong> The service is provided “as is”
                without warranty of any kind. PointsPilot is not liable for missed
                flights, lost points, denied awards, or any other loss arising from
                use of the beta.
              </p>
            </div>
          </section>

          {/* NDA */}
          <section className="space-y-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-brand-ink/60">
              Mutual Non-Disclosure (Beta)
            </h2>
            <div className="bg-brand-cream/60 border border-brand-ink/10 rounded-xl p-4 text-sm text-brand-ink/80 space-y-3 max-h-56 overflow-y-auto leading-relaxed">
              <p>
                <strong>Confidential information.</strong> The PointsPilot beta —
                including the user interface, features, AI prompts and outputs,
                roadmap, pricing strategy, sweet spots database, source code,
                screenshots, and any non-public details discussed with you about the
                product — is confidential information of PointsPilot.
              </p>
              <p>
                <strong>Your obligations.</strong> You agree to: (a) keep the
                product and your access credentials private; (b) not share
                screenshots, videos, or descriptions of the product publicly or with
                anyone outside the beta program without prior written permission;
                (c) not reverse engineer, copy, or build a competing product based
                on the beta; and (d) use the beta only for personal trip planning
                and feedback.
              </p>
              <p>
                <strong>Term.</strong> These confidentiality obligations apply
                during the beta and for two (2) years after the beta ends, or until
                the corresponding information becomes public through no fault of
                yours, whichever comes first.
              </p>
              <p>
                <strong>No license.</strong> Nothing here grants you ownership of or
                a license to any PointsPilot intellectual property beyond personal
                use of the beta.
              </p>
            </div>
          </section>

          {/* Acceptance */}
          <section className="space-y-3 pt-2 border-t border-brand-ink/10">
            <label className="block">
              <div className="text-xs font-bold uppercase tracking-wider text-brand-ink/60 mb-1">
                Your full name (required)
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Type your full name to sign"
                className="w-full bg-navy-light border border-brand-ink/15 rounded-lg px-3 py-2.5 text-sm text-brand-ink placeholder-brand-ink/30 focus:outline-none focus:border-brand-blue"
              />
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-brand-blue"
              />
              <span className="text-sm text-brand-ink/80">
                I have read and agree to the <strong>Beta Tester Agreement</strong>
                {' '}above. I understand PointsPilot is beta software and I will
                verify all award details directly with airlines and hotels before
                booking.
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreeNda}
                onChange={(e) => setAgreeNda(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-brand-blue"
              />
              <span className="text-sm text-brand-ink/80">
                I agree to the <strong>Mutual NDA</strong> above and will keep the
                product, screenshots, and any non-public details confidential.
              </span>
            </label>

            <button
              onClick={handleAccept}
              disabled={!canAccept}
              className="w-full mt-2 px-4 py-3 bg-brand-ink text-brand-cream font-extrabold rounded-xl text-sm shadow-lg shadow-brand-ink/20 hover:bg-brand-blue transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Accept &amp; enter the beta
            </button>

            <p className="text-[11px] text-brand-ink/50 text-center">
              By clicking accept, you sign this agreement electronically. A
              timestamped record is stored in this browser.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
